import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'
import { describeRuleset, type Ruleset } from '../lib/ruleset'
import { formatDateTime } from '../lib/datetime'
import { usePageTitle } from '../lib/usePageTitle'

interface FieldPlayer {
  dg_id: number
  dg_rank_at_field: number | null
  withdrawn: boolean
  players: { full_name: string; country: string | null; region: string | null } | null
}

interface Tournament {
  id: string
  name: string
  course: string | null
  status: string
  lock_time: string
  ruleset: Ruleset
}

const REGION_LABEL: Record<string, string> = {
  USA: 'USA',
  EUROPE: 'Europa',
  ROW: 'Övriga',
}

export function Draft() {
  const { id = '' } = useParams()
  const { session } = useAuth()

  const [tournament, setTournament] = useState<Tournament | null>(null)
  const [field, setField] = useState<FieldPlayer[]>([])
  usePageTitle(tournament ? `Drafta laget ${tournament.name}` : 'Drafta laget')
  const [picks, setPicks] = useState<number[]>([])
  const [teamId, setTeamId] = useState<string | null>(null)
  const [teamStatus, setTeamStatus] = useState<string | null>(null)

  // Vilket gäng laget hör till. Triggern i 0022 fyller i det själv när man
  // är med i exakt ett gäng, men den som är med i flera måste välja — då
  // vägrar databasen hellre än gissar fel gängs tabell.
  const [groups, setGroups] = useState<Array<{ id: string; name: string }>>([])
  const [groupId, setGroupId] = useState<string | null>(null)

  const [violations, setViolations] = useState<string[]>([])
  const [validating, setValidating] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)
  const [search, setSearch] = useState('')

  const teamSize = tournament?.ruleset?.team_size ?? 6

  /**
   * RLS-avslag betyder i praktiken alltid en sak här: picks_open() sa nej,
   * alltså har anmälan stängt medan man höll på. "new row violates row-level
   * security policy for table teams" är obegripligt för alla som inte skrivit
   * policyn — och deltagarna är inte IT-folk.
   *
   * Admin märker det aldrig själv: teams_admin_all släpper igenom oavsett
   * picks_open, så den som testar med sitt eget adminkonto ser inte spärren.
   */
  function readableError(message: string): string {
    if (/row-level security/i.test(message)) {
      return 'Anmälan är stängd för den här tävlingen, så laget gick inte att spara. Ladda om sidan för att se vad som gäller.'
    }
    return message
  }
  // Måste spegla picks_open() i databasen: status = 'open' OCH now() <
  // lock_time. Tidsvillkoret saknades här, så när lock_time passerat såg
  // draftvyn fullt redigerbar ut och felet kom först vid sparandet — som ett
  // RLS-fel, alltså obegripligt för den som inte skrivit policyn.
  //
  // Klockan är webbläsarens och kan gå fel. Det spelar ingen roll: det här
  // styr bara vad som visas, databasen avgör.
  const lockPassed = tournament ? new Date(tournament.lock_time).getTime() <= Date.now() : false
  const picksOpen = tournament?.status === 'open' && !lockPassed
  /*
   * Inlämnat är inte samma sak som låst.
   *
   * Databasen har hela tiden tillåtit ändringar fram till lock_time —
   * teams_update_own kräver picks_open(), inte "inte inlämnad". Vyn låste
   * ändå vid inlämning, så en spelare som drog sig ur på onsdagen gick inte
   * att byta ut trots att anmälan var öppen. Ett avhopp är just det som
   * gör att man vill ändra.
   */
  const locked = !picksOpen

  useEffect(() => {
    async function load() {
      const [t, f] = await Promise.all([
        supabase
          .from('tournaments')
          .select('id, name, course, status, lock_time, ruleset')
          .eq('id', id)
          .maybeSingle(),
        supabase
          .from('tournament_field')
          .select('dg_id, dg_rank_at_field, withdrawn, players(full_name, country, region)')
          .eq('tournament_id', id)
          .order('dg_rank_at_field', { nullsFirst: false }),
      ])

      if (t.error) setError(t.error.message)
      else setTournament(t.data as unknown as Tournament)

      if (f.error) setError(f.error.message)
      else setField((f.data as unknown as FieldPlayer[]) ?? [])

      if (session) {
        const { data: team } = await supabase
          .from('teams')
          .select('id, status, group_id, team_players(dg_id, slot)')
          .eq('tournament_id', id)
          .eq('user_id', session.user.id)
          .maybeSingle()

        if (team) {
          setTeamId(team.id as string)
          setTeamStatus(team.status as string)
          setGroupId((team.group_id as string | null) ?? null)
          const tp = (team.team_players ?? []) as Array<{ dg_id: number; slot: number }>
          setPicks([...tp].sort((a, b) => a.slot - b.slot).map((x) => x.dg_id))
        }

        // Filtret på user_id är inte en optimering. RLS på group_members
        // släpper igenom HELA gänget, så utan det kommer en rad per medlem
        // tillbaka och väljaren fylls med samma gäng en gång per medlem —
        // varpå list.length > 1 och alla får frågan "vilket gäng?" trots
        // att de bara är med i ett. Samma filter finns i Leaderboard och
        // TournamentChat.
        const { data: gm } = await supabase
          .from('group_members')
          .select('groups(id, name)')
          .eq('user_id', session.user.id)
          .order('joined_at')
        const list = ((gm ?? []) as unknown as Array<{
          groups: { id: string; name: string } | Array<{ id: string; name: string }> | null
        }>).flatMap((r) => (Array.isArray(r.groups) ? r.groups : r.groups ? [r.groups] : []))
        setGroups(list)
        // Ett gäng: inget att välja. Flera: låt hen välja aktivt i stället
        // för att förvälja det första och hoppas att det blir rätt.
        const only = list.length === 1 ? list[0] : null
        if (only) setGroupId((prev) => prev ?? only.id)
      }

      setLoading(false)
    }

    void load()
  }, [id, session])

  // Regelmotorn ligger i databasen. Draftvyn frågar den i stället för att
  // härma reglerna — då kan de inte glida isär.
  useEffect(() => {
    if (picks.length === 0) {
      setViolations([])
      return
    }

    let cancelled = false
    setValidating(true)

    const t = setTimeout(async () => {
      const { data, error } = await supabase.rpc('validate_picks', {
        p_tournament_id: id,
        p_dg_ids: picks,
      })
      if (cancelled) return
      setViolations(error ? [error.message] : ((data as string[]) ?? []))
      setValidating(false)
    }, 250)

    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [picks, id])

  const toggle = useCallback(
    (dgId: number) => {
      if (locked) return
      setSaved(false)
      setPicks((prev) =>
        prev.includes(dgId)
          ? prev.filter((x) => x !== dgId)
          : prev.length >= teamSize
            ? prev
            : [...prev, dgId],
      )
    },
    [locked, teamSize],
  )

  async function save(submit: boolean) {
    if (!session) return
    setBusy(true)
    setError(null)

    let tid = teamId
    if (!tid) {
      const { data, error } = await supabase
        .from('teams')
        .insert({
          tournament_id: id,
          user_id: session.user.id,
          status: 'draft',
          // Null är rätt när man är med i noll eller ett gäng — triggern
          // avgör. Är man med i flera avvisar den i stället för att gissa.
          group_id: groupId,
        })
        .select('id')
        .single()
      if (error) {
        setError(readableError(error.message))
        setBusy(false)
        return
      }
      tid = data.id as string
      setTeamId(tid)
    }

    /*
     * Ett inlämnat lag går tillbaka till utkast INNAN spelarna rörs.
     *
     * enforce_team_rules validerar bara övergången till submitted, och
     * hoppar medvetet över uppdateringar av ett redan inlämnat lag —
     * annars hade live-scoring-workerns skrivningar varje minut kört
     * regelmotorn. Det betyder att byten på ett inlämnat lag aldrig
     * kontrolleras. Genom att gå via draft och tillbaka körs motorn igen.
     *
     * Ordningen är inte kosmetisk: avbryts sparandet mitt i står laget kvar
     * som utkast och räknas inte, i stället för att stå som inlämnat med en
     * uppsättning ingen kontrollerat.
     */
    if (teamStatus === 'submitted') {
      const { error: dErr } = await supabase
        .from('teams')
        .update({ status: 'draft' })
        .eq('id', tid)

      if (dErr) {
        setError(readableError(dErr.message))
        setBusy(false)
        return
      }
      setTeamStatus('draft')
    }

    await supabase.from('team_players').delete().eq('team_id', tid)

    const { error: tpErr } = await supabase.from('team_players').insert(
      picks.map((dgId, i) => {
        const fp = field.find((x) => x.dg_id === dgId)
        return {
          team_id: tid,
          dg_id: dgId,
          slot: i + 1,
          dg_rank_at_pick: fp?.dg_rank_at_field ?? null,
          region_at_pick: fp?.players?.region ?? null,
        }
      }),
    )

    if (tpErr) {
      setError(readableError(tpErr.message))
      setBusy(false)
      return
    }

    if (submit) {
      // Triggern kör samma regelmotor igen. Går den inte igenom här hade
      // valideringen ovan fel, och då är det triggern som har rätt.
      const { error: sErr } = await supabase
        .from('teams')
        .update({ status: 'submitted' })
        .eq('id', tid)

      if (sErr) {
        setError(readableError(sErr.message))
        setBusy(false)
        return
      }
      setTeamStatus('submitted')
    }

    setSaved(true)
    setBusy(false)
  }

  const selected = useMemo(
    () => picks.map((dgId) => field.find((f) => f.dg_id === dgId)).filter(Boolean) as FieldPlayer[],
    [picks, field],
  )

  const rankSum = selected.reduce((s, p) => s + (p.dg_rank_at_field ?? 999), 0)
  const regionCounts = selected.reduce<Record<string, number>>((acc, p) => {
    const r = p.players?.region ?? 'ROW'
    acc[r] = (acc[r] ?? 0) + 1
    return acc
  }, {})

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return field.filter(
      (f) => !q || (f.players?.full_name ?? '').toLowerCase().includes(q),
    )
  }, [field, search])

  if (loading) return <div className="page"><p className="muted">Hämtar fältet…</p></div>
  if (!tournament) return <div className="page"><p className="muted">Turneringen finns inte.</p></div>

  // Utan gäng går laget inte att skapa när man är med i flera — databasen
  // avvisar det, så knapparna ska inte låtsas att det går.
  const groupMissing = groups.length > 1 && !groupId
  const valid = violations.length === 0 && picks.length === teamSize && !groupMissing

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">{tournament.course ?? 'Draft'}</p>
        <h1>{tournament.name}</h1>
      </div>

      {error && <div className="notice notice--error">{error}</div>}

      {!picksOpen && (
        <div className="notice notice--error">
          {lockPassed && tournament.status === 'open'
            ? `Anmälan stängde ${formatDateTime(tournament.lock_time)}.`
            : 'Laginlämningen är stängd för den här turneringen.'}
        </div>
      )}

      {/* Bara för den som är med i flera gäng. Är man med i ett fyller
          triggern i det, och då är en meny bara i vägen. */}
      {groups.length > 1 && (
        <section className="card stack">
          <h2>Vilket gäng?</h2>
          <p className="muted">
            Du är med i flera gäng. Laget räknas i det du väljer här.
          </p>
          <select
            value={groupId ?? ''}
            onChange={(e) => setGroupId(e.target.value || null)}
            disabled={locked || teamId !== null}
          >
            <option value="">Välj gäng…</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
          {teamId !== null && (
            <p className="muted">
              Gänget är låst när laget väl är skapat. Hör av dig om det blivit fel.
            </p>
          )}
        </section>
      )}

      <section className="card stack">
        <h2>Reglerna</h2>
        <ul className="muted" style={{ paddingLeft: 18 }}>
          {describeRuleset(tournament.ruleset).map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </section>

      <section className="card stack">
        <h2>
          Ditt lag {picks.length}/{teamSize}
        </h2>

        {selected.length === 0 && <p className="muted">Inga spelare valda än.</p>}

        {selected.map((p, i) => (
          <div key={p.dg_id} className="pick">
            <span className="pick__slot">{i + 1}</span>
            <span className="pick__name">{p.players?.full_name}</span>
            <span className="pick__meta">
              {p.players?.country} · {REGION_LABEL[p.players?.region ?? 'ROW']} · rank{' '}
              {p.dg_rank_at_field ?? 999}
            </span>
            {!locked && (
              <button className="link" onClick={() => toggle(p.dg_id)}>
                Ta bort
              </button>
            )}
          </div>
        ))}

        {selected.length > 0 && (
          <p className="muted">
            Rankingsumma {rankSum} ·{' '}
            {Object.entries(regionCounts)
              .map(([r, n]) => `${REGION_LABEL[r] ?? r} ${n}`)
              .join(' · ')}
          </p>
        )}

        {violations.length > 0 && (
          <div className="notice notice--error">
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {violations.map((v) => (
                <li key={v}>{v}</li>
              ))}
            </ul>
          </div>
        )}

        {valid && !validating && (
          <div className="notice notice--ok">Laget uppfyller alla regler.</div>
        )}

        {saved && <p className="muted">Sparat.</p>}

        {!locked && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {/* "Spara utkast" göms för ett inlämnat lag. Det enda den hade
                gjort är att göra laget till ett utkast igen, och den som
                sedan glömmer att lämna in på nytt står utan lag i tävlingen
                utan att ha bett om det. */}
            {teamStatus !== 'submitted' && (
              <button
                onClick={() => void save(false)}
                disabled={busy || picks.length === 0 || groupMissing}
              >
                Spara utkast
              </button>
            )}
            {/* `validating` måste med. Utan den är knappen klickbar under
                debouncen medan `violations` fortfarande håller svaret för
                FÖRRA uppsättningen — byter man en spelare hinner man lämna in
                ett lag som just blivit ogiltigt, och triggern avvisar det med
                ett felmeddelande i stället för att knappen bara varit grå.
                Den gröna rutan nedan väntade redan in svaret; knappen gjorde
                inte det. */}
            <button
              className="primary"
              onClick={() => void save(true)}
              disabled={busy || !valid || validating}
            >
              {busy
                ? 'Skickar…'
                : validating
                  ? 'Kontrollerar…'
                  : teamStatus === 'submitted'
                    ? 'Spara ändringen'
                    : 'Lämna in laget'}
            </button>
          </div>
        )}

        {teamStatus === 'submitted' && !locked && (
          <div className="notice notice--ok">
            Laget är inlämnat — och går att ändra fram till {formatDateTime(tournament?.lock_time)}.
            Drar sig en spelare ur byter du ut hen här och sparar om.
          </div>
        )}

        {teamStatus === 'submitted' && locked && (
          <div className="notice notice--ok">
            Laget är inlämnat. Inlämningen stängde {formatDateTime(tournament?.lock_time)} och
            laget går inte längre att ändra.
          </div>
        )}

        {teamStatus !== 'submitted' && locked && (
          <div className="notice notice--error">
            Inlämningen stängde {formatDateTime(tournament?.lock_time)}. Laget är kvar som
            utkast och räknas inte i tävlingen.
          </div>
        )}
      </section>

      <section className="card stack">
        <h2>Fältet ({field.length})</h2>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Sök spelare"
        />

        <div className="field-list">
          {visible.map((f) => {
            const chosen = picks.includes(f.dg_id)
            return (
              <button
                key={f.dg_id}
                className={`field-row${chosen ? ' field-row--chosen' : ''}`}
                onClick={() => toggle(f.dg_id)}
                disabled={locked || f.withdrawn || (!chosen && picks.length >= teamSize)}
              >
                <span className="field-row__rank">{f.dg_rank_at_field ?? '—'}</span>
                <span className="field-row__name">
                  {f.players?.full_name}
                  {f.withdrawn && ' (ur)'}
                </span>
                <span className="field-row__meta">
                  {f.players?.country} · {REGION_LABEL[f.players?.region ?? 'ROW']}
                </span>
              </button>
            )
          })}
        </div>
      </section>
    </div>
  )
}
